import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { ApolloLink } from "apollo-link";
import { fetchAuthSession } from "aws-amplify/auth";
import { AUTH_TYPE, createAuthLink } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import localForage from "localforage";
import _ from "lodash";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import AppRoutes from "./AppRoutes";
import Loader from "./components/common/Loader/Loader";
import WebViewRoute from "./components/WebViewRoute/WebViewRoute";
import Configuration from "./config";
import LayoutContainer from "./containers/LayoutContainer";
import { UserProvider } from "./contexts/UserContext";
import { CookieLanguageInfoEnum, LanguagesEnum } from "./helpers/enums";
import { getCookie, getPageName, setCookie } from "./helpers/helpers";
import useAmplifyAuth from "./helpers/useAmplifyAuth";
import { useLogger } from "./hooks/useLogger";
import { persistAuth } from "./reducers/auth";
import { languageHandler } from "./reducers/language";
import store from "./store";

import "./App.scss";

import { Amplify } from "aws-amplify";
import i18n from "i18next";
import ReactGA from "react-ga";
import WebViewContainer from "./containers/WebViewContainer";
import { PASSWORD_EXPRIRATION } from "./helpers/queries";
import { useKeyboardNavigationClass } from "./helpers/useKeyboardNavigationClass";
import { RootState } from "./rootReducer";

//@todo only for develop removed in realm ENV
// setCookie({name: 'x-app-webview-active', value: true, options: []});
// setCookie({name: 'x-app-webview-authorization', value: "eyJraWQiOiJCNzFpK0NIU3hWMkhDUUdCTEYzbCtlTEFCb1wvWURQU01NMjMzYmg2YlNDND0iLCJhbGciOiJSUzI1NiJ9.eyJzdWIiOiI1NTFlZGY2MC03ZmFkLTQ5NDItOTZhMy1hZDU0NzE3MzFjMDQiLCJyb2xlIjoiUFJJVkFURSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJjdXN0b206bGFzdExvZ2luIjoiMjAyMi0wNS0zMVQxNDo0NjozNC45ODlaIiwiY3VzdG9tOnNhZm9BY2NvdW50SWQiOiJhMk4zWDAwMDAwSlNUaUxVQVgiLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAuZXUtY2VudHJhbC0xLmFtYXpvbmF3cy5jb21cL2V1LWNlbnRyYWwtMV9sWU1mVWJxSlYiLCJwaG9uZV9udW1iZXJfdmVyaWZpZWQiOmZhbHNlLCJjb2duaXRvOnVzZXJuYW1lIjoidGVzdC5wcml2YXRvIiwiYXV0aF9pc3N1ZXIiOiJDT0dOSVRPIiwicGljdHVyZSI6InByb3RlY3RlZFwvZXUtY2VudHJhbC0xOjUxYmE2YmExLTllNTMtNDIyOC1hOGM2LTM4MzZlOTJmZTg4ZFwvcHJvZmlsZVBpY3R1cmUtMTY0MzEwMzgwNy5qcGciLCJhdWQiOiIyMWpoZDVpcDlncm8yNm9wbXQ3MjRrbXEwbSIsImV2ZW50X2lkIjoiZTQ3YTkzYzItOGI5ZS00NzE4LWEyYTUtZjc0ODEyNDZjZDhhIiwidG9rZW5fdXNlIjoiaWQiLCJhdXRoX3RpbWUiOjE2NTQwMDg0MTcsIm5hbWUiOiJUZXN0LlByaXZhdG8iLCJjdXN0b206dXBkYXRlZEVtYWlsVmVyaWZpZWQiOiJ0cnVlIiwiZXhwIjoxNjU0MDEyMDE3LCJpYXQiOjE2NTQwMDg0MTcsImVtYWlsIjoidGVzdC5wcml2YXRvQHlvcG1haWwuY29tIn0.QEoSOdbajbebkputXUiUs_dYMK8eiyAhSMopHTnJ-84Lioj_sK7jbqQydgvUG9AtdFe9MtKLqp51EJGvWNxJNDurrVsEd-bkzDGVJlI82BdIIppVfDS4b011JSu88GllYeERQqJ5yQRrixrimFT6d-7E_6fIuRQIsWEcSPT5Hj_P_J5fiQOmEDP-G26m3GvUZV24ldoKbv1rNMBRqthQVJTUQHMrrwxG7vqj3sa-EQgxX8FStTBDLlzVKcXkfpZi-0s6oRr2WGZF6BQaxS0-EhRilUyJwBqCV8hixrVhjL-S-ZkidEva9yzyhGfdQlPsJVNKmM77lIkZ_v3DQn70AA", options: []});
/** REMOVE IN REALM ENV **/

const getAccessToken = async (): Promise<string | null> => {
  const auth: any = store.getState().auth;
  if (auth.appAuthorizationToken) {
    return new Promise((resolve) => {
      return resolve("Bearer " + auth.appAuthorizationToken);
    });
  }
  try {
    const session = await fetchAuthSession();

    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      return null;
    }

    return "Bearer " + idToken;
  } catch (error) {
    console.log("getAccessToken - ERROR - ", error);
    return null;
  }
};
let clientInstance: ApolloClient<NormalizedCacheObject> | null = null;

// Apollo client
export const client = (removeHeader = false) => {
  if (clientInstance && !removeHeader) {
    return clientInstance;
  }

  const impersonationMiddleware = new ApolloLink((operation, forward) => {
    const auth: any = store.getState().auth;
    const { impersonationToken } = auth;
    const apiKey: any =
      Configuration.get("REACT_APP_API_KEY") || "only-for-test";

    if (impersonationToken) {
      if (removeHeader) {
        operation.setContext({
          headers: {
            "x-api-key": apiKey,
            "X-CAP-Channel": "WEB",
          },
        });
      } else {
        operation.setContext({
          headers: {
            "X-Cap-Impersonate": impersonationToken,
            "x-api-key": apiKey,
            "X-CAP-Channel": "WEB",
          },
        });
      }
    }
    return forward(operation);
  });
  const apiKeyMiddleware = new ApolloLink((operation, forward) => {
    const apiKey: any =
      Configuration.get("REACT_APP_API_KEY") || "only-for-test";
    operation.setContext({
      headers: {
        "x-api-key": apiKey,
        "X-CAP-Channel": "WEB",
      },
    });
    return forward(operation);
  });
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: Configuration.get("REACT_APP_USERPOOLID"),
        userPoolClientId: Configuration.get("REACT_APP_USERPOOL_WEB_CLIENTID"),
        identityPoolId: Configuration.get("REACT_APP_IDENTITYPOOLID"),
      },
    },
    Storage: {
      S3: {
        region: Configuration.get("REACT_APP_STORAGE_REGION"),
        bucket: Configuration.get("REACT_APP_STORAGE_BUCKET"),
      },
    },
  });

  const config = {
    url: Configuration.get("REACT_APP_APPSYNC_GRAPHQL_ENDPOINT"),
    region: Configuration.get("REACT_APP_AUTH_REGION"),
    auth: {
      type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
      jwtToken: getAccessToken,
    },
    disableOffline: true,
  };

  const link = ApolloLink.from([
    apiKeyMiddleware.concat(impersonationMiddleware),
    createAuthLink(config as any) as any,
    createSubscriptionHandshakeLink(config as any) as any,
  ]);

  const newClient = new ApolloClient({
    devtools: {
      enabled: true,
    },
    link: link as any,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            getCustomer: { merge: true },
            getOperator: { merge: true },
            getPaymentDetail: { merge: true },
            retrieveBankDomiciliation: { merge: true },
          },
        },

        AccountHolder: {
          keyFields: false,
          merge: true,
        },
        ContractAddress: {
          keyFields: false,
          merge: true,
        },
        Customer: {
          keyFields: false,
          merge: true,
        },
        Operator: {
          keyFields: false,
          merge: true,
        },

        Contract: {
          keyFields: ["contractId"],
          fields: {
            invoices: { merge: false },
            metre: { merge: false },

            serviceOnlineBill: { merge: true },
            serviceBankDomiciliation: { merge: true },

            accountHolder: { merge: true },
            puf: { merge: true },
          },
        },

        Invoices: { keyFields: false, merge: false },
        Consumptions: { keyFields: false, merge: false },

        RetrieveCaseRes: { keyFields: false, merge: true },
        ContractMetre: { keyFields: false, merge: true },
        UserAccountDetail: { keyFields: false, merge: true },
        AddressType: { keyFields: false, merge: true },
        PufAddress: { keyFields: false, merge: true },
      },
    }),
  });

  if (!removeHeader) {
    clientInstance = newClient;
  }

  return newClient;
};

const App = () => {
  const log = useLogger("App");
  const { fetching, handleSignout } = useAmplifyAuth();
  const [rehydrated, setRehydrated] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const userFetched = useSelector((state: RootState) => state.auth.userFetched);
  const user = useSelector((state: RootState) => state.auth.user);

  const { t } = useTranslation();
  const activeWebviewLayout = getCookie("x-app-webview-active");
  const activeWebviewAuthorization = getCookie("x-app-webview-authorization");
  const webviewLanguage = getCookie("x-app-webview-language");

  const providerLanguage = (lang: any) => {
    if (!lang) {
      return LanguagesEnum.default;
    }
    switch (lang) {
      case "it":
      case "en_IT":
        return LanguagesEnum.it;
      default:
        return LanguagesEnum.en;
    }
  };

  const loadLanguage = getCookie(CookieLanguageInfoEnum.name);

  if (webviewLanguage) {
    if (providerLanguage(webviewLanguage) !== loadLanguage) {
      setCookie({
        name: CookieLanguageInfoEnum.name,
        value: providerLanguage(getCookie("x-app-webview-language")),
        options: {
          path: CookieLanguageInfoEnum.path,
        },
      });
      dispatch(
        languageHandler(providerLanguage(getCookie("x-app-webview-language")))
      );
    }
  } else if (!loadLanguage) {
    setCookie({
      name: CookieLanguageInfoEnum.name,
      value: LanguagesEnum.default,
      options: {
        path: CookieLanguageInfoEnum.path,
      },
    });
    dispatch(languageHandler(LanguagesEnum.default));
  }

  const loadPersistData = useCallback(async () => {
    const persist: any = await localForage.getItem("state");
    if (persist) {
      dispatch(persistAuth(persist.impersonation));
    }
    return true;
  }, [dispatch]);
  const checkURL = useCallback(
    (locationPath: string) => {
      if (userFetched) {
        if (
          (locationPath === "/login" ||
            locationPath === "/register" ||
            locationPath === "/forgot-password") &&
          user
        ) {
          console.log(
            "Utente rilevato in Redux su pagina login. Verifico sessione reale..."
          );

          fetchAuthSession()
            .then((session) => {
              if (session.tokens?.idToken) {
                console.log("Sessione confermata. Redirect a dashboard.");
                history.push("/dashboard");
              } else {
                throw new Error("No token found");
              }
            })
            .catch(() => {
              console.log(
                "LOOOP EVITATO: Redux dice loggato, ma la sessione è morta."
              );
            });

          return;
        }

        if (!_.isObject(locationPath)) {
          // we need this check to avoid location objects being pushed to gtm
          const titles = getPageName(locationPath);
          document.title = `${t(titles.siteTitle)}${
            titles.pageTitle ? " - " + t(titles.pageTitle) : ""
          }`;

          if (Configuration.get("REACT_APP_ENVIRONMENT") === "production") {
            ReactGA.pageview(window.location.pathname + window.location.search);
          }
        }
      }
    },
    [history, t, user, userFetched, dispatch]
  );

  useEffect(() => {
    if (user) {
      client()
        .query({
          query: PASSWORD_EXPRIRATION,
        })
        .then(
          (data: {
            data: { passwordExpirationCheck: { isExpired: boolean } };
          }) => {
            if (data.data.passwordExpirationCheck.isExpired) {
              return history.push({
                pathname: "change-password",
                state: { passwordExpiration: true },
              });
            }
          }
        );
    }
    const unlisten: any = history.listen((location: any) => {
      // console.log("on route change: ", location);
      log.info("URL Changed: ", location);
      checkURL(location);
    });
    if (!rehydrated) {
      loadPersistData()
        .then()
        .catch((err) => console.error(err));
      setRehydrated(true);
    }
    return function cleanup() {
      unlisten();
    };
  }, [history, rehydrated, log, checkURL, loadPersistData, user]);

  useEffect(() => {
    document.documentElement.lang = i18n.language.split("-")[0];
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    checkURL(location.pathname);
  }, [location.pathname, checkURL]);
  useKeyboardNavigationClass();

  useEffect(() => {
    const path = (/#!\/area-riservata(\/.*)$/.exec(window.location.hash) ||
      [])[1];
    if (path) {
      history.replace(path);
    }
  }, [history]);
  if (activeWebviewLayout || activeWebviewAuthorization) {
    console.log(activeWebviewLayout, "ACTIVE");
    return (
      <WebViewContainer
        user={user}
        userFetched={fetching}
        handleSignout={handleSignout}
      >
        <UserProvider value={!!user}>
          <WebViewRoute />
        </UserProvider>
      </WebViewContainer>
    );
  } else {
    return (
      <LayoutContainer
        user={user}
        userFetched={fetching}
        handleSignout={handleSignout}
      >
        <UserProvider value={!!user}>
          <AppRoutes />
        </UserProvider>
      </LayoutContainer>
    );
  }
};

const WithProvider = () => {
  return (
    <ApolloProvider client={client()}>
      <Suspense fallback={<Loader />}>
        <App />
      </Suspense>
    </ApolloProvider>
  );
};

export default WithProvider;
